import React from 'react';
import { Box, Chip, List, ListItem } from '@mui/material';
import { Modal } from '../new/modal/Modal';
import { ModalTitle } from '../new/modal/ModalTitle';
import { ModalContent } from '../new/modal/ModalContent';
import { ModalFooter } from '../new/modal/ModalFooter';
import { Typography } from '../new/Typography';
import { UserUploadedAddresses } from '../../utils/constant';
import WarningIcon from '@mui/icons-material/Warning';

interface InvalidAddressesAlertProps {
  open: boolean;
  onClose: () => void;
  /** Combined list of addresses that block navigation:
   *  - isValidAddress === false && !isUserApproved  → "Invalid Address"
   *  - isTooManyLines === true                      → "Too Many Lines"
   */
  invalidAddresses: UserUploadedAddresses[];
  /**
   * Called when the user clicks on an address row in the popup.
   * Opens the edit drawer pre-populated with that address.
   */
  onEditAddress: (address: UserUploadedAddresses) => void;
}

/**
 * Blocking popup shown when one or more user-uploaded addresses require review
 * before the user can proceed. Covers both invalid addresses (Bedrock AI flagged)
 * and over-limit addresses (> 5 content lines from file upload).
 *
 * Uses the same Modal + ModalTitle + ModalContent + ModalFooter pattern as the
 * rest of the application. Clicking an address row opens the edit drawer directly.
 *
 * Requirements: FR-9, FR-10, FR-11
 */
export const InvalidAddressesAlert: React.FC<InvalidAddressesAlertProps> = ({
  open,
  onClose,
  invalidAddresses,
  onEditAddress,
}) => {
  const count = invalidAddresses.length;

  const handleAddressClick = (addr: UserUploadedAddresses) => {
    onClose();
    // Defer so the Dialog close animation completes before the drawer opens.
    setTimeout(() => onEditAddress(addr), 0);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle onClose={onClose}>Addresses Require Review</ModalTitle>
      <ModalContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Typography>
            {count === 1
              ? 'There is 1 address that requires review. Please fix it before continuing.'
              : `There are ${count} addresses that require review. Please fix them before continuing.`}
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#49454F' }}>
            Click an address to edit it:
          </Typography>
          <List dense disablePadding>
            {invalidAddresses.map((addr) => {
              const reason: 'tooManyLines' | 'invalid' = addr.isTooManyLines === true ? 'tooManyLines' : 'invalid';
              return (
                <ListItem
                  key={addr.id}
                  disableGutters
                  onClick={() => handleAddressClick(addr)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    mb: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '0.0625rem solid #FFE8E7',
                    background: '#FFF8F7',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': {
                      background: '#FFE8E7',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', mb: '0.25rem' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#5E0A05', flex: 1 }}>
                      {addr.name}
                    </Typography>
                    <Chip
                      icon={<WarningIcon sx={{ fontSize: '0.75rem' }} />}
                      label={reason === 'tooManyLines' ? 'Too Many Lines' : 'Invalid Address'}
                      size="small"
                      sx={{
                        color: '#5E0A05',
                        background: '#FFE8E7',
                        '& .MuiChip-icon': { color: '#5E0A05' },
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                  {addr.address.slice(0, 3).map((line, i) => (
                    <Typography key={i} sx={{ fontSize: '0.8125rem', color: '#49454F' }}>
                      {line}
                    </Typography>
                  ))}
                  <Typography sx={{ fontSize: '0.75rem', color: '#436eb2', mt: '0.25rem', fontWeight: 500 }}>
                    Click to edit →
                  </Typography>
                </ListItem>
              );
            })}
          </List>
          <Typography sx={{ fontSize: '0.8125rem', color: '#49454F' }}>
            For invalid addresses, fix the issue or use &quot;Approve Address&quot; to proceed as-is. For addresses with too many lines, reduce to 5 lines or fewer.
          </Typography>
        </Box>
      </ModalContent>
      <ModalFooter
        onSubmit={() => handleAddressClick(invalidAddresses[0])}
        submitText="Review Addresses"
      />
    </Modal>
  );
};

export default InvalidAddressesAlert;
